// MissionReady Radio — pure state layer for the live push-to-talk session.
//
// Kept free of browser APIs so the lifecycle can be unit tested. The WebRTC
// transport (radio-webrtc.ts), Deepgram streaming (radio-deepgram.ts) and the
// React hook (useRadioLive) all drive this reducer.

export type RadioPhase =
  | "idle"
  | "connecting"
  | "ready"
  | "transmitting"
  | "stopping"
  | "error";

export type RadioErrorKind =
  | "mic-denied"
  | "mic-unavailable"
  | "connection"
  | "authorization"
  | "unknown";

export type RadioParticipant = {
  userId: string;
  name: string;
  state: "connected" | "available" | "transmitting" | "disconnected";
};

export type RadioState = {
  phase: RadioPhase;
  channelId: string | null;
  siteId: string | null;
  /** Ephemeral presence — never persisted as messages. */
  participants: RadioParticipant[];
  /** Interim Deepgram transcript for the in-flight transmission only. */
  interim: string;
  /** Finalized segments of the in-flight transmission. */
  finals: string[];
  transmissionStartedAt: string | null;
  transcriptionAvailable: boolean;
  error: { kind: RadioErrorKind; message: string } | null;
};

export const initialRadioState: RadioState = {
  phase: "idle",
  channelId: null,
  siteId: null,
  participants: [],
  interim: "",
  finals: [],
  transmissionStartedAt: null,
  transcriptionAvailable: true,
  error: null,
};

export type RadioAction =
  | { type: "connect"; siteId: string }
  | { type: "connected"; channelId: string }
  | { type: "disconnected" }
  | { type: "presence"; participants: RadioParticipant[] }
  | { type: "transmit-start"; startedAt: string }
  | { type: "transmit-stopping" }
  | { type: "transmit-done" }
  | { type: "interim"; text: string }
  | { type: "final"; text: string }
  | { type: "transcription-unavailable" }
  | { type: "error"; kind: RadioErrorKind; message: string }
  | { type: "clear-error" };

export function radioReducer(state: RadioState, action: RadioAction): RadioState {
  switch (action.type) {
    case "connect":
      return { ...state, phase: "connecting", siteId: action.siteId, error: null };
    case "connected":
      return { ...state, phase: "ready", channelId: action.channelId, error: null };
    case "disconnected":
      return { ...initialRadioState, siteId: state.siteId };
    case "presence":
      return { ...state, participants: action.participants };
    case "transmit-start":
      return {
        ...state,
        phase: "transmitting",
        transmissionStartedAt: action.startedAt,
        interim: "",
        finals: [],
        transcriptionAvailable: true,
        error: null,
      };
    case "transmit-stopping":
      return state.phase === "transmitting" ? { ...state, phase: "stopping" } : state;
    case "transmit-done":
      return {
        ...state,
        phase: state.channelId ? "ready" : "idle",
        transmissionStartedAt: null,
        interim: "",
        finals: [],
      };
    case "interim":
      return { ...state, interim: action.text };
    case "final":
      return action.text.trim()
        ? { ...state, finals: [...state.finals, action.text.trim()], interim: "" }
        : { ...state, interim: "" };
    case "transcription-unavailable":
      return { ...state, transcriptionAvailable: false, interim: "" };
    case "error":
      return { ...state, phase: "error", error: { kind: action.kind, message: action.message } };
    case "clear-error":
      return { ...state, phase: state.channelId ? "ready" : "idle", error: null };
    default:
      return state;
  }
}

/** Transmission is only possible from a connected, non-busy Radio context. */
export function canTransmit(state: RadioState): boolean {
  return state.phase === "ready" && !!state.channelId;
}

/** Live text shown while transmitting (finals + current interim). */
export function liveTranscript(state: RadioState): string {
  return [...state.finals, state.interim].filter(Boolean).join(" ").trim();
}

/** Final transcript persisted once per transmission; interim text is dropped. */
export function assembleFinalTranscript(finals: string[]): string {
  return finals
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

export function radioErrorCopy(kind: RadioErrorKind): string {
  switch (kind) {
    case "mic-denied":
      return "Microphone permission required for Radio";
    case "mic-unavailable":
      return "Microphone unavailable on this device";
    case "connection":
      return "Radio connection unavailable";
    case "authorization":
      return "You're not authorized for this site's Radio";
    default:
      return "Radio unavailable";
  }
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
